import { useMemo } from 'react';
import { usePatientSubscription } from './usePatientSubscription';
import { useRoomData } from './useRoomData';
import { useNotificationTemplates } from './useNotificationTemplates';
import { useScreenConfiguration } from './useScreenConfiguration';
import { mapPatientData } from '../pacuAndPrep/mapPatients';

/**
 * Combined hook that fetches all data needed for the Room Tablet component.
 * This replaces the entire HOC chain from the legacy implementation.
 *
 * @param roomType - The type of room (PRE_OP, PACU, POST_OP, or undefined for all)
 * @param roomId - The ID of the room (used for room details query)
 * @param filterByRoom - If true, filter patients to the specific roomId. If false, get all patients for roomType.
 *                       Default is true for kiosk screens (specific room), false for overview screens (all rooms of type).
 * @returns Object containing all data needed by the component
 */
export function useRoomTabletData(
  roomType: string | undefined,
  roomId: number | undefined,
  filterByRoom: boolean = true
) {
  // Fetch all data using individual hooks
  // For patient subscription: only pass roomId if filterByRoom is true
  // This allows overview screens (like PrepAppScreen) to show patients from ALL rooms of the type
  const patientRoomId = filterByRoom ? roomId : undefined;
  const { patients: rawPatients, loading: patientsLoading } = usePatientSubscription(roomType, patientRoomId);
  const { room, loading: roomLoading } = useRoomData(roomId);
  const { patientPickupMessage, readyForFamilyMessage } = useNotificationTemplates();
  const { screenConfiguration } = useScreenConfiguration();

  // Transform patients data (same as legacy mapPatientData), applying the
  // optional physician filter from screen configuration. An empty/absent
  // showPhysicians list means "show all physicians".
  const patients = useMemo(() => {
    const showPhysicians = screenConfiguration?.showPhysicians;
    const showPhysiciansSet =
      showPhysicians && showPhysicians.length > 0 ? new Set(showPhysicians.map((id: number) => String(id))) : null;
    const filtered = showPhysiciansSet
      ? rawPatients.filter(p => p?.physician?.id != null && showPhysiciansSet.has(String(p.physician.id)))
      : rawPatients;
    return mapPatientData(filtered);
  }, [rawPatients, screenConfiguration]);

  return {
    patients,
    room,
    patientPickupMessage,
    readyForFamilyMessage,
    screenConfiguration,
    loading: patientsLoading || roomLoading,
  };
}
